import React, { useState, useEffect } from 'react';
import { Mail, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import clientSelfServiceService from '../../../services/clientSelfServiceService';

const PasswordResetWorkflow = () => {
  const [step, setStep] = useState('request'); // request, confirm, success
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const maxAttempts = 3;

  // Check URL for confirmation action
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params?.get('action') === 'confirm') {
      setStep('confirm');
    }
  }, []);

  // Validate password on change
  useEffect(() => {
    if (newPassword) {
      const validation = clientSelfServiceService?.validatePasswordStrength(newPassword);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [newPassword]);

  const handleRequestReset = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (attemptCount >= maxAttempts) {
      setError('Maximum attempts reached. Please try again in 15 minutes.');
      return;
    }

    if (!email?.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    const result = await clientSelfServiceService?.requestPasswordReset(email);
    setLoading(false);

    if (result?.success) {
      setSuccess('Password reset email sent! Please check your inbox and spam folder.');
      setStep('request');
      setAttemptCount(0);
    } else {
      setAttemptCount(prev => prev + 1);
      setError(result?.message || 'Failed to send reset email. Please try again.');
    }
  };

  const handleUpdatePassword = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordValidation?.isValid) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);
    const result = await clientSelfServiceService?.updatePassword(newPassword);
    setLoading(false);

    if (result?.success) {
      setSuccess('Password updated successfully! You can now log in with your new password.');
      setStep('success');
    } else {
      setError(result?.message || 'Failed to update password. Please try again.');
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'text-red-600 dark:text-red-400';
      case 'medium': return 'text-amber-600 dark:text-amber-400';
      case 'strong': return 'text-emerald-600 dark:text-emerald-400';
      default: return 'text-gray-500';
    }
  };

  const getStrengthWidth = (strength) => {
    switch (strength) {
      case 'weak': return 'w-1/3';
      case 'medium': return 'w-2/3';
      case 'strong': return 'w-full';
      default: return 'w-0';
    }
  };

  return (
    <div className="space-y-6">
      {/* Request Reset Step */}
      {step === 'request' && (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Password Reset Process</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Enter your registered email address. We'll send you a secure link to reset your password.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="your.email@company.com"
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">{success}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={loading || attemptCount >= maxAttempts}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            {attemptCount > 0 && attemptCount < maxAttempts && (
              <p className="text-xs text-muted-foreground text-center">
                Attempts remaining: {maxAttempts - attemptCount}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Confirm New Password Step */}
      {step === 'confirm' && (
        <div className="space-y-6">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-emerald-900 dark:text-emerald-100 mb-1">Email Verified</h4>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Please enter your new password below.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                New Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e?.target?.value)}
                  placeholder="Enter new password"
                  className="pl-10 pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {passwordValidation && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Password Strength:</span>
                  <span className={`font-medium ${getStrengthColor(passwordValidation?.strength)}`}>
                    {passwordValidation?.strength?.toUpperCase()}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      passwordValidation?.strength === 'weak' ? 'bg-red-500' :
                      passwordValidation?.strength === 'medium'? 'bg-amber-500' : 'bg-emerald-500'
                    } ${getStrengthWidth(passwordValidation?.strength)}`}
                  />
                </div>
                {passwordValidation?.errors?.length > 0 && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-medium">Requirements:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {passwordValidation?.errors?.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Confirm New Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={loading || !passwordValidation?.isValid}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </div>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Password Reset Successful!</h3>
            <p className="text-muted-foreground">
              Your password has been updated. You can now log in to your account with your new password.
            </p>
          </div>
          <Button
            variant="default"
            onClick={() => window.location.href = '/compliance-overview'}
            className="w-full"
          >
            Go to Login
          </Button>
        </div>
      )}
    </div>
  );
};

export default PasswordResetWorkflow;